import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import type { EditClientForm } from "@/lib/core/crm/types/contacts";

interface ClientContractSectionProps {
  editForm: UseFormReturn<EditClientForm>;
  isOpen?: boolean;
  onToggle?: () => void;
  headerless?: boolean;
}

const Fields: React.FC<{ editForm: UseFormReturn<EditClientForm> }> = ({ editForm }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="edit-contract">Contract Type</Label>
      <ResponsiveCommandSelect
        value={editForm.watch("contract_type") || ""}
        onValueChange={(val) =>
          editForm.setValue("contract_type", val || undefined, { shouldValidate: true })
        }
        options={[
          { value: "hourly", label: "Hourly" },
          { value: "monthly", label: "Monthly" },
          { value: "project", label: "Project" },
          { value: "retainer", label: "Retainer" },
        ]}
        placeholder="Select contract type"
        title="Contract Type"
        description="Choose the billing arrangement for this client"
      />
      <input type="hidden" {...editForm.register("contract_type")} />
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-hourly_rate">Hourly Rate ($)</Label>
      <Input
        id="edit-hourly_rate"
        type="number"
        step="0.01"
        min="0"
        {...editForm.register("hourly_rate", {
          valueAsNumber: true,
          min: { value: 0, message: "Rate must be positive" },
        })}
        placeholder="Hourly Rate"
        aria-describedby={
          editForm.formState.errors.hourly_rate ? "hourly-rate-error" : undefined
        }
      />
      {editForm.formState.errors.hourly_rate && (
        <p id="hourly-rate-error" className="text-sm text-red-600">
          {editForm.formState.errors.hourly_rate.message}
        </p>
      )}
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-monthly_rate">Monthly Rate ($)</Label>
      <Input
        id="edit-monthly_rate"
        type="number"
        step="0.01"
        min="0"
        {...editForm.register("monthly_rate", {
          valueAsNumber: true,
          min: { value: 0, message: "Rate must be positive" },
        })}
        placeholder="Monthly Rate"
        aria-describedby={
          editForm.formState.errors.monthly_rate ? "monthly-rate-error" : undefined
        }
      />
      {editForm.formState.errors.monthly_rate && (
        <p id="monthly-rate-error" className="text-sm text-red-600">
          {editForm.formState.errors.monthly_rate.message}
        </p>
      )}
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-payment_terms">Payment Terms</Label>
      <Input
        id="edit-payment_terms"
        {...editForm.register("payment_terms")}
        placeholder="Payment Terms"
      />
    </div>
    <div className="space-y-2">
      <Label htmlFor="edit-tax_id">Tax ID</Label>
      <Input id="edit-tax_id" {...editForm.register("tax_id")} placeholder="Tax ID" />
    </div>
  </>
);

export const ClientContractSection: React.FC<ClientContractSectionProps> = ({
  editForm,
  isOpen,
  onToggle,
  headerless,
}) => {
  if (headerless) {
    return (
      <div className="space-y-4">
        <Fields editForm={editForm} />
      </div>
    );
  }
  return (
    <div className="border rounded-lg">
      <button
        type="button"
        className="w-full flex justify-between items-center px-4 py-3 bg-card-50 hover:bg-card-100 font-semibold text-left"
        onClick={onToggle}
      >
        Contract & Financial
        <span>{isOpen ? "−" : "+"}</span>
      </button>
      {isOpen && (
        <div className="p-4 space-y-4">
          <Fields editForm={editForm} />
        </div>
      )}
    </div>
  );
};
